import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { productionAPI } from '../services/api';
import './Production.css';

interface BatchAllocation {
  id: number;
  order_number: string;
  order_id: number;
  line_number: string;
  customer_name: string;
  customer_id: number;
  allocated_weight: number;
  package_type: string;
  number_of_packages: number;
  delivery_date: string;
  status: string;
}

interface ProductionBatch {
  id: number;
  batch_number: string;
  formula_id: number;
  formula_name: string;
  feed_form: string;
  planned_weight: number;
  actual_weight: number | null;
  status: string;
  status_display: string;
  planned_date: string | null;
  priority: number;
  started_at: string | null;
  completed_at: string | null;
  completed_by: string | null;
  notes: string;
  allocations: BatchAllocation[];
  orders_count: number;
  total_allocated: number;
  ship_dates: string[];
}

interface ScheduleData {
  start_date: string;
  end_date: string;
  batches: ProductionBatch[];
  batches_by_date: { [date: string]: ProductionBatch[] };
  total_batches: number;
  status_summary: {
    planned: number;
    in_production: number;
    completed: number;
    packaging: number;
  };
}

type ViewType = 'today' | 'this_week' | 'next_week' | 'all';

const Production: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [scheduleData, setScheduleData] = useState<ScheduleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeView, setActiveView] = useState<ViewType>(
    (searchParams.get('view') as ViewType) || 'this_week'
  );
  const [selectedBatch, setSelectedBatch] = useState<ProductionBatch | null>(null);

  const loadSchedule = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await productionAPI.getSchedule({ view: activeView });
      setScheduleData(response.data);
    } catch (err: any) {
      console.error('Error loading schedule:', err);
      setError('Failed to load production schedule');
    } finally {
      setLoading(false);
    }
  }, [activeView]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  const handleViewChange = (view: ViewType) => {
    setActiveView(view);
    setSearchParams({ view });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateFull = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today - " + date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return "Tomorrow - " + date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }

    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'PLANNED':
        return 'status-planned';
      case 'IN_PRODUCTION':
      case 'IN_PROGRESS':
        return 'status-in-production';
      case 'COMPLETED':
        return 'status-completed';
      case 'PACKAGING':
      case 'PACKAGED':
        return 'status-packaged';
      case 'SHIPPED':
        return 'status-shipped';
      default:
        return '';
    }
  };

  const renderBatchCard = (batch: ProductionBatch) => (
    <div
      key={batch.id}
      className={`batch-card ${getStatusClass(batch.status)}`}
      onClick={() => setSelectedBatch(batch)}
    >
      <div className="batch-card-header">
        <div className="batch-info">
          <span className="batch-number">{batch.batch_number}</span>
          <span className="batch-formula">{batch.formula_name}</span>
          <span className={`batch-form form-${batch.feed_form.toLowerCase()}`}>
            {batch.feed_form}
          </span>
        </div>
        <span className={`batch-status ${getStatusClass(batch.status)}`}>
          {batch.status_display}
        </span>
      </div>

      <div className="batch-card-body">
        <div className="batch-weight">
          <span className="weight-value">{batch.planned_weight.toLocaleString()}</span>
          <span className="weight-unit">lbs</span>
        </div>
        <div className="batch-orders">
          {batch.orders_count} order{batch.orders_count !== 1 ? 's' : ''}
        </div>
      </div>

      <div className="batch-card-footer">
        <div className="ship-dates">
          Ship: {batch.ship_dates.slice(0, 3).map(d => formatDate(d)).join(', ')}
          {batch.ship_dates.length > 3 && ` +${batch.ship_dates.length - 3} more`}
        </div>
        <div className="batch-actions">
          <button
            className="btn btn-sm btn-secondary"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedBatch(batch);
            }}
          >
            View Details
          </button>
        </div>
      </div>
    </div>
  );

  if (loading && !scheduleData) {
    return (
      <div className="production-page">
        <div className="production-loading">
          <div className="loading-spinner"></div>
          <p>Loading production schedule...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="production-page">
      {/* Header */}
      <div className="production-header">
        <div className="header-content">
          <h1>Production Schedule</h1>
          <p className="header-subtitle">View Only - Use Mill Portal for production controls</p>
        </div>
        <div className="header-actions">
          <a
            href="/mill/dashboard"
            className="btn btn-primary"
          >
            Go to Mill Portal
          </a>
        </div>
      </div>

      {/* View Tabs */}
      <div className="view-tabs">
        <button
          className={`tab ${activeView === 'today' ? 'active' : ''}`}
          onClick={() => handleViewChange('today')}
        >
          Today
        </button>
        <button
          className={`tab ${activeView === 'this_week' ? 'active' : ''}`}
          onClick={() => handleViewChange('this_week')}
        >
          This Week
        </button>
        <button
          className={`tab ${activeView === 'next_week' ? 'active' : ''}`}
          onClick={() => handleViewChange('next_week')}
        >
          Next Week
        </button>
        <button
          className={`tab ${activeView === 'all' ? 'active' : ''}`}
          onClick={() => handleViewChange('all')}
        >
          All Planned
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Status Summary */}
      {scheduleData && (
        <div className="status-summary">
          <div className="summary-card planned">
            <span className="summary-count">{scheduleData.status_summary.planned}</span>
            <span className="summary-label">Planned</span>
          </div>
          <div className="summary-card in-production">
            <span className="summary-count">{scheduleData.status_summary.in_production}</span>
            <span className="summary-label">In Production</span>
          </div>
          <div className="summary-card completed">
            <span className="summary-count">{scheduleData.status_summary.completed}</span>
            <span className="summary-label">Completed</span>
          </div>
          <div className="summary-card packaging">
            <span className="summary-count">{scheduleData.status_summary.packaging}</span>
            <span className="summary-label">Packaging</span>
          </div>
        </div>
      )}

      {/* Batches by Date */}
      {scheduleData && scheduleData.batches_by_date && (
        <div className="batches-container">
          {Object.entries(scheduleData.batches_by_date)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([dateStr, batches]) => (
              <div key={dateStr} className="date-section">
                <h2 className="date-header">{formatDateFull(dateStr)}</h2>
                <div className="batches-grid">
                  {batches.map(batch => renderBatchCard(batch))}
                </div>
              </div>
            ))}

          {Object.keys(scheduleData.batches_by_date).length === 0 && (
            <div className="no-batches">
              <p>No batches scheduled for this period.</p>
              <a
                href="/mill/dashboard"
                className="btn btn-primary"
              >
                View Production Plan in Mill Portal
              </a>
            </div>
          )}
        </div>
      )}

      {/* Batch Detail Modal */}
      {selectedBatch && (
        <div className="modal-overlay" onClick={() => setSelectedBatch(null)}>
          <div className="modal-content batch-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedBatch.batch_number}</h2>
              <button className="modal-close" onClick={() => setSelectedBatch(null)}>
                &times;
              </button>
            </div>

            <div className="modal-body">
              <div className="batch-detail-header">
                <div>
                  <h3>{selectedBatch.formula_name}</h3>
                  <span className={`batch-form form-${selectedBatch.feed_form.toLowerCase()}`}>
                    {selectedBatch.feed_form}
                  </span>
                </div>
                <span className={`batch-status-large ${getStatusClass(selectedBatch.status)}`}>
                  {selectedBatch.status_display}
                </span>
              </div>

              <div className="batch-detail-info">
                <div className="info-row">
                  <span className="info-label">Planned Weight:</span>
                  <span className="info-value">{selectedBatch.planned_weight.toLocaleString()} lbs</span>
                </div>
                {selectedBatch.actual_weight && (
                  <div className="info-row">
                    <span className="info-label">Actual Weight:</span>
                    <span className="info-value">{selectedBatch.actual_weight.toLocaleString()} lbs</span>
                  </div>
                )}
                <div className="info-row">
                  <span className="info-label">Planned Date:</span>
                  <span className="info-value">
                    {selectedBatch.planned_date ? formatDateFull(selectedBatch.planned_date) : 'Not set'}
                  </span>
                </div>
              </div>

              <div className="batch-allocations">
                <h4>Orders in This Batch:</h4>
                <table className="allocations-table">
                  <thead>
                    <tr>
                      <th>Order #</th>
                      <th>Customer</th>
                      <th>Weight</th>
                      <th>Package</th>
                      <th>Ship Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBatch.allocations.map((alloc) => (
                      <tr key={alloc.id}>
                        <td>
                          <a href={`/orders/${alloc.order_id}`} onClick={(e) => {
                            e.preventDefault();
                            setSelectedBatch(null);
                            navigate(`/orders/${alloc.order_id}`);
                          }}>
                            #{alloc.order_number}
                          </a>
                        </td>
                        <td>{alloc.customer_name}</td>
                        <td>{alloc.allocated_weight.toLocaleString()} lbs</td>
                        <td>
                          {alloc.package_type === 'BULK' ? 'Bulk' : `${alloc.number_of_packages} ${alloc.package_type}`}
                        </td>
                        <td>{formatDate(alloc.delivery_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2}><strong>Total Allocated:</strong></td>
                      <td colSpan={3}><strong>{selectedBatch.total_allocated.toLocaleString()} lbs</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {selectedBatch.notes && (
                <div className="batch-notes">
                  <h4>Notes:</h4>
                  <p>{selectedBatch.notes}</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <a
                href="/mill/dashboard"
                className="btn btn-primary"
              >
                Go to Mill Portal
              </a>
              <button className="btn btn-secondary" onClick={() => setSelectedBatch(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Production;
